import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  Mail, 
  MapPin,
  ArrowUp,
  X 
} from 'lucide-react';

const services = [
  'Engineering & Design',
  'Construction Services',
  'Network Fulfillment',
  'Maintenance & Support', 
  'Project Management',
  'Emergency Response'
];


export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  
  const handleNavClick = (section) => {
    console.log(`Footer navigation: ${section}`);
  };


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    console.log('Scroll to top clicked');
  };

  return (
    <footer className="bg-muted/50 border-t">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-primary">Fiber Methods</h3>
            <p className="text-muted-foreground leading-relaxed">
              Connecting America through professional telecom infrastructure services. 
              Expert engineering, construction, and maintenance solutions since 2008.
            </p>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Services</h4>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li 
                  key={index}
                  className="text-muted-foreground"
                  data-testid={`footer-service-${index}`}
                >
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Contact Info</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <a href="tel:+17042755737" className="text-foreground font-medium hover:text-primary transition-colors" data-testid="phone-link-footer">(704) 275-5737</a>
                  <p className="text-sm text-muted-foreground">24/7 Emergency Line</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <a href="mailto:info@fiberm.com" className="text-foreground font-medium hover:text-primary transition-colors" data-testid="email-link-footer">info@fiberm.com</a>
                  <p className="text-sm text-muted-foreground">General Inquiries</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-foreground font-medium">933 Louise Ave, Ste 101</p>
                  <p className="text-foreground">Charlotte, NC 28204</p>
                  <p className="text-sm text-muted-foreground">Headquarters</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-border pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-muted-foreground">
              <p>&copy; {currentYear} Fiber Methods. All rights reserved.</p>
              <div className="flex space-x-6">
                <button 
                  onClick={() => setIsPrivacyModalOpen(true)}
                  className="hover:text-primary transition-colors"
                  data-testid="footer-privacy"
                >
                  Privacy Policy
                </button>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={scrollToTop}
              className="flex items-center space-x-2"
              data-testid="button-scroll-top"
            >
              <span>Back to Top</span>
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Privacy Policy Modal */}
        {isPrivacyModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/80" 
              onClick={() => setIsPrivacyModalOpen(false)}
            ></div>
            
            {/* Modal Content */}
            <div className="relative bg-background border rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Privacy Policy</h2>
                <button
                  onClick={() => setIsPrivacyModalOpen(false)}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 p-1"
                  data-testid="close-privacy-modal"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
              
              {/* Content */}
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">1. Information We Collect</h3>
                  <p className="mb-2">When you submit a message through the contact form on our website, we may collect the following information:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Your full name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Company name</li>
                    <li>Service request details</li>
                    <li>Any other information you voluntarily provide</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">2. How We Use Your Information</h3>
                  <p className="mb-2">We use the information you provide to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Respond to your inquiry</li>
                    <li>Understand your project needs</li>
                    <li>Contact you about our services</li>
                    <li>Maintain internal records related to business operations</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">3. Cookies & Tracking Technologies</h3>
                  <p className="mb-2">We may use cookies, pixels, and similar tracking technologies to enhance your browsing experience, analyze website traffic, and support advertising efforts.</p>
                  <p className="mb-2">These technologies may collect limited information about your visit, such as browser type, device, pages visited, and interactions with our website. This data may be used to help us improve our services and deliver more relevant ads.</p>
                  <p>You can adjust your browser settings to refuse cookies or notify you when cookies are being used. By continuing to use our website, you consent to our use of these technologies.</p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">4. Sharing Your Information with Third‑Party Service Providers</h3>
                  <p className="mb-2">We may share your information with trusted third‑party service providers that assist us in operating our website, delivering services, communicating with you, or conducting other business activities.</p>
                  <p className="mb-2">These providers are only permitted to use your information as necessary to perform services for us and are not authorized to use your information for their own marketing purposes.</p>
                  <p>We do not sell or rent your information to unrelated third parties for advertising.</p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">5. Data Security</h3>
                  <p>We take reasonable measures to protect the information you submit against unauthorized access or disclosure. However, no method of transmission over the internet is 100% secure.</p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">6. Contact</h3>
                  <p>If you have any questions about this policy or how we handle your data, please contact us at <a href="mailto:info@fiberm.com" className="text-primary hover:underline">info@fiberm.com</a>.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}